import { html, render, type TemplateResult } from 'lit-html';
import styles from './pk-tab.css?inline';

/**
 * `<pk-tab>` — single tab inside a `<pk-tab-set>` container.
 *
 * Renders as an anchor-styled button. The parent `<pk-tab-set>` is responsible
 * for tracking the active href, applying the `active` attribute to the
 * matching child, and forwarding click → `pk-tab-change`.
 *
 * Attributes:
 *   - `label` (required) — visible tab text.
 *   - `href`  (required) — the destination URL or tab identifier.
 *   - `active` (boolean) — set by `<pk-tab-set>` when this tab matches its
 *                          current `active` attribute. Consumers shouldn't
 *                          set this directly.
 *   - `disabled` (boolean) — disables click + sets aria-disabled.
 */
export class PkTab extends HTMLElement {
    static readonly tagName = 'pk-tab';

    static get observedAttributes(): string[] {
        return ['label', 'href', 'active', 'disabled'];
    }

    private readonly _root: ShadowRoot;

    constructor() {
        super();
        this._root = this.attachShadow({ mode: 'open' });
    }

    connectedCallback(): void {
        this.render();
    }

    attributeChangedCallback(): void {
        this.render();
    }

    private template(): TemplateResult {
        const label    = this.getAttribute('label') ?? '';
        const href     = this.getAttribute('href')  ?? '';
        const active   = this.hasAttribute('active');
        const disabled = this.hasAttribute('disabled');

        return html`
            <style>${styles}</style>
            <a
                class="tab ${active ? 'active' : ''} ${disabled ? 'disabled' : ''}"
                href=${href}
                role="tab"
                aria-selected=${active ? 'true' : 'false'}
                aria-disabled=${disabled ? 'true' : 'false'}
                tabindex=${disabled ? '-1' : '0'}
            >${label}</a>
        `;
    }

    private render(): void {
        render(this.template(), this._root);
    }
}

if (!customElements.get(PkTab.tagName)) {
    customElements.define(PkTab.tagName, PkTab);
}

declare global {
    interface HTMLElementTagNameMap {
        'pk-tab': PkTab;
    }
}
